import { z } from 'zod';

/**
 * The largest image the platform will hand a vendor, in base64 characters.
 *
 * ~8 MB of encoded text, comfortably above a phone camera's output after the
 * upload pipeline has accepted it, and low enough that a malformed or hostile
 * asset cannot make one call carry an unbounded body.
 */
const MAX_IMAGE_BASE64_CHARS = 8_000_000;

/**
 * An image as every image-taking AI request carries it (`embedImage`,
 * `classifyImage`).
 *
 * The image travels as **base64 in the request** rather than as an id or a URL,
 * deliberately: `shared/ai` never learns where bytes come from, so it cannot
 * grow a dependency on `media` (U1/N12), and the cache key is then a hash of
 * the image itself — a member who resubmits the same photograph is served from
 * the cache instead of paying the vendor twice.
 */
export const AiImageSchema = z
  .object({
    /** Lower-case, parameter-free — what `normalizeMime` produces. */
    mime: z.string().regex(/^image\/[a-z0-9.+-]+$/),
    dataBase64: z.string().min(1).max(MAX_IMAGE_BASE64_CHARS),
  })
  .strict();

export type AiImage = z.infer<typeof AiImageSchema>;
